import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import { isRouteRestrictedForNonRoot } from "../utils/rolePermissions";
import { usePermissions } from "../utils/permissions";
import { RbacLevel, CapabilityKey } from "../utils/rbacCatalog";
import { HiOutlineLockClosed } from "react-icons/hi";

interface RoleProtectedRouteProps {
  children: ReactNode;
  requireCapability?: CapabilityKey;
  capabilityLevel?: RbacLevel;
  requireAnyCapability?: Array<{
    capability: CapabilityKey;
    level?: RbacLevel;
  }>;
  requireOwner?: boolean;
  fallbackPath?: string;
  showAlert?: boolean;
}

export const RoleProtectedRoute = ({
  children,
  requireCapability,
  capabilityLevel = "view",
  requireAnyCapability,
  requireOwner,
  fallbackPath = "/control-panel",
  showAlert = true,
}: RoleProtectedRouteProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const location = useLocation();
  const isRootUser = permissions.isRootUser();

  const deny = (messageKey: string) => {
    if (showAlert) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <HiOutlineLockClosed className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t("roleProtection.accessDenied")}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t(messageKey)}
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  };

  if (requireAnyCapability && requireAnyCapability.length > 0) {
    const allowed = requireAnyCapability.some(({ capability, level }) =>
      permissions.can(capability, level ?? "view")
    );
    if (!allowed) return deny("roleProtection.noAccess");
  } else if (
    requireCapability &&
    !permissions.can(requireCapability, capabilityLevel)
  ) {
    return deny("roleProtection.noAccess");
  }

  if (requireOwner && permissions.isRoleGoverned()) {
    return deny("roleProtection.noAccess");
  }

  if (!isRootUser && isRouteRestrictedForNonRoot(location.pathname)) {
    if (showAlert) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <HiOutlineLockClosed className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t("roleProtection.accessDenied")}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("roleProtection.rootOnlyAccess")}
            </p>
          </div>
        </div>
      );
    }

    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
